/**
 * Profile Management Admin Component
 *
 * Allows users to view, create, edit, and delete custom node and switch profiles.
 */

import { useState, useMemo } from 'react';
import { Section } from '../layout/Section';
import { Table } from '../display/Table';
import {
  getCustomNodeProfilesSync,
  getCustomSwitchProfilesSync,
  deleteCustomNodeProfile,
  deleteCustomSwitchProfile,
  exportCustomProfiles,
  importCustomProfiles,
  clearAllCustomProfiles,
  type CustomNodeProfile,
  type CustomSwitchProfile,
} from '../../services/customProfileStorage';
import { ALL_NODE_PROFILES } from '../../data/nodes';
import { ALL_SWITCHES } from '../../data/switches';
import { NodeProfileEditor } from './NodeProfileEditor';
import { SwitchProfileEditor } from './SwitchProfileEditor';

export function ProfileManagement() {
  const [activeTab, setActiveTab] = useState<'nodes' | 'switches'>('nodes');
  const [customNodes, setCustomNodes] = useState<CustomNodeProfile[]>(getCustomNodeProfilesSync());
  const [customSwitches, setCustomSwitches] = useState<CustomSwitchProfile[]>(getCustomSwitchProfilesSync());
  const [editingNode, setEditingNode] = useState<CustomNodeProfile | null>(null);
  const [editingSwitch, setEditingSwitch] = useState<CustomSwitchProfile | null>(null);
  const [showNodeEditor, setShowNodeEditor] = useState(false);
  const [showSwitchEditor, setShowSwitchEditor] = useState(false);

  // Combine built-in and custom profiles
  const allNodeProfiles = useMemo(() => {
    const builtinNodes = ALL_NODE_PROFILES.map(n => ({ ...n, builtin: true }));
    return [...builtinNodes, ...customNodes];
  }, [customNodes]);

  const allSwitchProfiles = useMemo(() => {
    const builtinSwitches = ALL_SWITCHES.map(s => ({ ...s, builtin: true }));
    return [...builtinSwitches, ...customSwitches];
  }, [customSwitches]);

  // Refresh custom profiles from storage
  const refreshProfiles = () => {
    setCustomNodes(getCustomNodeProfilesSync());
    setCustomSwitches(getCustomSwitchProfilesSync());
  };

  // Handle node profile actions
  const handleCreateNode = () => {
    setEditingNode(null);
    setShowNodeEditor(true);
  };

  const handleEditNode = (profile: CustomNodeProfile) => {
    // If editing a builtin profile, create a copy with a new ID
    if (profile.builtin) {
      const timestamp = Date.now();
      const customCopy = {
        ...profile,
        id: `${profile.id}-custom-${timestamp}`,
        name: `${profile.name} (Custom)`,
        builtin: false,
      };
      setEditingNode(customCopy as CustomNodeProfile);
    } else {
      setEditingNode(profile);
    }
    setShowNodeEditor(true);
  };

  const handleDeleteNode = (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete the node profile "${name}"?`)) {
      try {
        deleteCustomNodeProfile(id);
        refreshProfiles();
      } catch (error) {
        alert('Failed to delete profile: ' + error);
      }
    }
  };

  const handleNodeEditorClose = (saved: boolean) => {
    setShowNodeEditor(false);
    setEditingNode(null);
    if (saved) {
      refreshProfiles();
    }
  };

  // Handle switch profile actions
  const handleCreateSwitch = () => {
    setEditingSwitch(null);
    setShowSwitchEditor(true);
  };

  const handleEditSwitch = (profile: CustomSwitchProfile) => {
    // If editing a builtin profile, create a copy with a new ID
    if (profile.builtin) {
      const timestamp = Date.now();
      const customCopy = {
        ...profile,
        id: `${profile.id}-custom-${timestamp}`,
        productName: `${profile.productName} (Custom)`,
        builtin: false,
      };
      setEditingSwitch(customCopy as CustomSwitchProfile);
    } else {
      setEditingSwitch(profile);
    }
    setShowSwitchEditor(true);
  };

  const handleDeleteSwitch = (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete the switch profile "${name}"?`)) {
      try {
        deleteCustomSwitchProfile(id);
        refreshProfiles();
      } catch (error) {
        alert('Failed to delete profile: ' + error);
      }
    }
  };

  const handleSwitchEditorClose = (saved: boolean) => {
    setShowSwitchEditor(false);
    setEditingSwitch(null);
    if (saved) {
      refreshProfiles();
    }
  };

  // Export/Import handlers
  const handleExport = () => {
    try {
      const json = exportCustomProfiles();
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `custom-profiles-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to export profiles: ' + error);
    }
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const json = event.target?.result as string;
          const result = importCustomProfiles(json);
          alert(`Imported ${result.nodes} node profiles and ${result.switches} switch profiles`);
          refreshProfiles();
        } catch (error) {
          alert('Failed to import profiles: ' + error);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleClearAll = () => {
    if (confirm('Are you sure you want to delete ALL custom profiles? This cannot be undone.')) {
      try {
        clearAllCustomProfiles();
        refreshProfiles();
      } catch (error) {
        alert('Failed to clear profiles: ' + error);
      }
    }
  };

  // Prepare node table rows
  const nodeRows = allNodeProfiles.map(profile => [
    profile.name,
    profile.vendor,
    profile.role,
    profile.rackUnits + 'U',
    profile.powerKw + ' kW',
    profile.description,
    profile.builtin ? (
      <div className="flex gap-2 items-center">
        <span className="text-xs text-gray-500">Built-in</span>
        <button
          onClick={() => handleEditNode(profile as CustomNodeProfile)}
          className="text-xs text-blue-600 hover:text-blue-800"
          title="Clone this profile to create a custom version"
        >
          Clone
        </button>
      </div>
    ) : (
      <div className="flex gap-2">
        <button
          onClick={() => handleEditNode(profile as CustomNodeProfile)}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          Edit
        </button>
        <button
          onClick={() => handleDeleteNode(profile.id, profile.name)}
          className="text-xs text-red-600 hover:text-red-800"
        >
          Delete
        </button>
      </div>
    ),
  ]);

  // Prepare switch table rows
  const switchRows = allSwitchProfiles.map(profile => [
    profile.productName,
    profile.vendor,
    profile.ports.totalPorts,
    profile.ports.speed.displayName,
    profile.fabricType,
    profile.power.typicalWatts + 'W',
    profile.builtin ? (
      <div className="flex gap-2 items-center">
        <span className="text-xs text-gray-500">Built-in</span>
        <button
          onClick={() => handleEditSwitch(profile as CustomSwitchProfile)}
          className="text-xs text-blue-600 hover:text-blue-800"
          title="Clone this profile to create a custom version"
        >
          Clone
        </button>
      </div>
    ) : (
      <div className="flex gap-2">
        <button
          onClick={() => handleEditSwitch(profile as CustomSwitchProfile)}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          Edit
        </button>
        <button
          onClick={() => handleDeleteSwitch(profile.id, profile.productName)}
          className="text-xs text-red-600 hover:text-red-800"
        >
          Delete
        </button>
      </div>
    ),
  ]);

  return (
    <div className="p-4">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Profile Management</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage custom node and switch profiles. Clone built-in profiles to create editable versions.
            </p>
          </div>

          {/* Export/Import/Clear buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleExport}
              className="px-3 py-2 text-xs font-semibold bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            >
              Export Custom
            </button>
            <button
              onClick={handleImport}
              className="px-3 py-2 text-xs font-semibold bg-green-100 text-green-700 rounded hover:bg-green-200"
            >
              Import
            </button>
            <button
              onClick={handleClearAll}
              className="px-3 py-2 text-xs font-semibold bg-red-100 text-red-700 rounded hover:bg-red-200"
            >
              Clear All Custom
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('nodes')}
            className={`px-4 py-2 font-semibold text-sm transition-colors ${
              activeTab === 'nodes'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Node Profiles ({allNodeProfiles.length})
          </button>
          <button
            onClick={() => setActiveTab('switches')}
            className={`px-4 py-2 font-semibold text-sm transition-colors ${
              activeTab === 'switches'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Switch Profiles ({allSwitchProfiles.length})
          </button>
        </div>

        {/* Node Profiles Tab */}
        {activeTab === 'nodes' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">
                {customNodes.length} custom, {allNodeProfiles.length - customNodes.length} built-in
              </p>
              <button
                onClick={handleCreateNode}
                className="px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                + Create Node Profile
              </button>
            </div>

            <Section title="Node Profiles">
              <Table
                headers={['Name', 'Vendor', 'Role', 'RU', 'Power', 'Description', 'Actions']}
                rows={nodeRows}
              />
            </Section>
          </div>
        )}

        {/* Switch Profiles Tab */}
        {activeTab === 'switches' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">
                {customSwitches.length} custom, {allSwitchProfiles.length - customSwitches.length} built-in
              </p>
              <button
                onClick={handleCreateSwitch}
                className="px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                + Create Switch Profile
              </button>
            </div>

            <Section title="Switch Profiles">
              <Table
                headers={['Product Name', 'Vendor', 'Ports', 'Speed', 'Fabric Type', 'Power', 'Actions']}
                rows={switchRows}
              />
            </Section>
          </div>
        )}
      </div>

      {/* Editors (modals) */}
      {showNodeEditor && (
        <NodeProfileEditor
          profile={editingNode}
          onClose={handleNodeEditorClose}
        />
      )}

      {showSwitchEditor && (
        <SwitchProfileEditor
          profile={editingSwitch}
          onClose={handleSwitchEditorClose}
        />
      )}
    </div>
  );
}
